<template>
  <sea-page title="Upload documento" header="Upload documento" back>
    <sea-page-content toolbar hideRapidSearch>
      <template v-slot:toolbar>
        <table-toolbar-button
          icon="mdi-cloud-upload"
          label="Upload"
          @click="save"
          :disabled="$v.editedItem.$invalid"
        ></table-toolbar-button>
      </template>
      <v-card flat>
        <form name="form" role="form" @submit.prevent="save()">
          <v-card-text>
            <v-container>
              <v-row>
                <v-col cols="12" sm="6" offset-sm="3">
                  <v-autocomplete
                    label="Azienda"
                    v-model="$v.editedItem.codAzienda.$model"
                    auto-select-first
                    :items="companyList"
                    @input="$v.editedItem.codAzienda.$touch()"
                    @blur="$v.editedItem.codAzienda.$touch()"
                    @change="updateEmployees"
                    required
                  ></v-autocomplete> </v-col
              ></v-row>
              <v-row>
                <v-col cols="12" sm="6" offset-sm="3">
                  <v-text-field
                    v-model="$v.editedItem.dataConsegna.$model"
                    label="Data pubblicazione"
                    type="date"
                    :error-messages="errors('dataConsegna')"
                    @input="$v.editedItem.dataConsegna.$touch()"
                    @blur="$v.editedItem.dataConsegna.$touch()"
                    required
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" sm="6" offset-sm="3">
                  <v-text-field
                    v-model="$v.editedItem.etichetta.$model"
                    label="Etichetta"
                    placeholder="max 15 caratteri"
                    :error-messages="errors('etichetta')"
                    @input="$v.editedItem.etichetta.$touch()"
                    @blur="$v.editedItem.etichetta.$touch()"
                    maxlength="15"
                    required
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" sm="6" offset-sm="3">
                  <v-text-field
                    v-model="$v.editedItem.descrizione.$model"
                    label="Titolo documento"
                    :error-messages="errors('descrizione')"
                    @input="$v.editedItem.descrizione.$touch()"
                    @blur="$v.editedItem.descrizione.$touch()"
                    maxlength="50"
                    required
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" sm="6" offset-sm="3">
                  <v-autocomplete
                    label="Lavoratore"
                    v-model="$v.editedItem.codiceFiscale.$model"
                    :items="employees"
                    clearable
                    open-on-clear
                    multiple
                    hide-selected
                    chips
                    deletable-chips
                    :error-messages="errors('codiceFiscale')"
                  ></v-autocomplete>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" sm="6" offset-sm="3">
                  <v-file-input
                    @input="$v.editedItem.file.$touch()"
                    @blur="$v.editedItem.file.$touch()"
                    v-model="$v.editedItem.file.$model"
                    :error-messages="errors('file')"
                    label="Allega file"
                  ></v-file-input>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
        </form>
        <v-overlay :value="overlay">
          <v-progress-circular indeterminate size="64"></v-progress-circular>
        </v-overlay>
      </v-card>
    </sea-page-content>
  </sea-page>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { validationMixin } from "vuelidate";
import { maxLength, required } from "vuelidate/lib/validators";
import mixin from "@/mixin";
import { MAX_FILE_SIZE } from "@/constants";

export default {
  mixins: [mixin, validationMixin],
  validations() {
    return {
      editedItem: {
        codAzienda: {
          required,
        },
        dataConsegna: {
          required,
          minValue(val) {
            return new Date(val) >= new Date(this.today());
          },
        },
        descrizione: {
          required,
          maxLength: maxLength(50),
        },
        etichetta: {
          required,
          maxLength: maxLength(15),
        },
        codiceFiscale: {},
        file: {
          required,
          fileSize(val) {
            if (!val) {
              return true;
            }
            let file = val;
            return file.size < MAX_FILE_SIZE;
          },
        },
      },
    };
  },
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      if (to.query.codiceAzienda) {
        vm.fetchCompanyList().then((res) => {
          vm.companyList = res.filter((az) =>
            to.query.codiceAzienda.includes(az.value)
          );
        });
      }
    });
  },
  data() {
    return {
      companyList: [],
      editedItem: {
        codAzienda: null,
        etichetta: null,
        descrizione: null,
        nomeFile: null,
        dataConsegna: null,
        codiceFiscale: null,
        file: null,
      },
      overlay: false,
    };
  },
  mounted() {
    this.editedItem.dataConsegna = this.today();
    this.resetCurrentCompany();
    this.resetEmployees();
  },
  destroyed() {},
  methods: {
    updateEmployees() {
      this.editedItem.codiceFiscale = null;
      this.setCurrentCompany(this.editedItem.codAzienda);
      this.fetchEmployees();
    },
    async save() {
      this.$v.$touch();
      if (!this.$v.editedItem.$invalid) {
        this.overlay = true;
        await this.send(this.editedItem);
        this.overlay = false;
        this.$router.back();
      }
    },
    errors(fieldName) {
      const errors = [];
      if (!this.$v.editedItem[fieldName].$dirty) return errors;
      if (fieldName === "dataConsegna") {
        !this.$v.editedItem.dataConsegna.required &&
          errors.push(this.locales.required);
        !this.$v.editedItem.dataConsegna.minValue &&
          errors.push("La data deve essere maggiore o uguale a quella odierna");
      } else if (fieldName === "codAzienda") {
        !this.$v.editedItem.codAzienda.required &&
          errors.push(this.locales.required);
      } else if (fieldName === "etichetta") {
        !this.$v.editedItem.etichetta.required &&
          errors.push(this.locales.required);
      } else if (fieldName === "descrizione") {
        !this.$v.editedItem.descrizione.required &&
          errors.push(this.locales.required);
      } else if (fieldName === "file") {
        !this.$v.editedItem.file.required && errors.push(this.locales.required);
        !this.$v.editedItem.file.fileSize && errors.push(this.locales.fileSize);
      }
      return errors;
    },
    ...mapActions({
      send: "docsLav/uploadMultiDocument",
      fetchCompanyList: "proxy/fetchCompanyList",
      fetchEmployees: "widget/fetchEmployees",
      setCurrentCompany: "widget/setCurrentCompany",
      resetEmployees: "widget/resetEmployees",
      resetCurrentCompany: "widget/resetCurrentCompany",
    }),
  },
  computed: {
    ...mapGetters({
      _employees: "widget/employees",
      companyId: "docsLav/currentCompany",
    }),
    employees() {
      return this._employees.map((employee) => {
        return {
          value: employee.codiceFiscale,
          text: `${employee.cognome} ${employee.nome}`,
        };
      });
    },
  },
};
</script>

<style>
/* */
</style>
